import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Recycle, Leaf, Tag, ShoppingCart, TreePine, Package, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

// Clearance items - pre-built enclosures from overstock timber
const CLEARANCE_ITEMS = [
  {
    id: "cl-001",
    name: "Eco Oak 3x2x2",
    description: "Built from surplus English Oak timber. Perfect for smaller species.",
    dimensions: { width: 3, height: 2, depth: 2 },
    shape: "rectangular",
    stain: "English Oak",
    stainKey: "english_oak",
    originalPrice: 485.00,
    clearancePrice: 339.50,
    discount: 30,
    stock: 2,
    timberSource: "Overstock from commercial furniture project",
    image: null,
  },
  {
    id: "cl-002",
    name: "Natural Pine 4x3x2",
    description: "Crafted from excess Golden Pine. Great mid-size enclosure.",
    dimensions: { width: 4, height: 3, depth: 2 },
    shape: "rectangular",
    stain: "Golden Pine",
    stainKey: "golden_pine",
    originalPrice: 598.00,
    clearancePrice: 448.50,
    discount: 25,
    stock: 3,
    timberSource: "End-of-line batch from sustainable forestry",
    image: null,
  },
  {
    id: "cl-003",
    name: "Walnut Corner Unit",
    description: "L-shaped corner enclosure in rich Dark Walnut. One-off piece.",
    dimensions: { width: 4, height: 4, depth: 2 },
    shape: "corner",
    stain: "Dark Walnut",
    stainKey: "dark_walnut",
    originalPrice: 758.00,
    clearancePrice: 492.70,
    discount: 35,
    stock: 1,
    timberSource: "Reclaimed from cancelled custom order",
    image: null,
  },
  {
    id: "cl-004",
    name: "Grey Driftwood 5x3x2",
    description: "Modern grey finish from surplus marine-grade plywood.",
    dimensions: { width: 5, height: 3, depth: 2 },
    shape: "rectangular",
    stain: "Driftwood Grey",
    stainKey: "driftwood_grey",
    originalPrice: 695.00,
    clearancePrice: 486.50,
    discount: 30,
    stock: 2,
    timberSource: "Overstock from boat building supplier",
    image: null,
  },
  {
    id: "cl-005",
    name: "Mahogany Hex Display",
    description: "Stunning hexagonal enclosure in Very Red Mahogany. Showroom piece.",
    dimensions: { width: 4, height: 4, depth: 2 },
    shape: "hexagonal",
    stain: "Very Red Mahogany",
    stainKey: "very_red_mahogany",
    originalPrice: 820.00,
    clearancePrice: 574.00,
    discount: 30,
    stock: 1,
    timberSource: "Display model - never used",
    image: null,
  },
  {
    id: "cl-006",
    name: "Natural Large 6x4x2",
    description: "Spacious enclosure in natural finish. Great for larger reptiles.",
    dimensions: { width: 6, height: 4, depth: 2 },
    shape: "rectangular",
    stain: "Natural",
    stainKey: "natural",
    originalPrice: 875.00,
    clearancePrice: 612.50,
    discount: 30,
    stock: 2,
    timberSource: "Excess from bulk timber purchase",
    image: null,
  },
];

const WOOD_STAINS_HEX = {
  english_oak: "#6B4423",
  golden_pine: "#D4A84B",
  dark_walnut: "#4A3728",
  driftwood_grey: "#78736E",
  very_red_mahogany: "#8B2500",
  natural: "#E3C28D",
};

const ClearancePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [loadingId, setLoadingId] = useState(null);

  const handleAddToCart = async (item) => {
    if (!user) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }

    setLoadingId(item.id);
    try {
      // Create config object for cart
      const config = {
        shape: item.shape,
        width: item.dimensions.width,
        height: item.dimensions.height,
        depth: item.dimensions.depth,
        stain_color: item.stainKey,
        glass_thickness: 6,
        is_clearance: true,
        clearance_id: item.id,
        clearance_price: item.clearancePrice,
      };
      
      await addToCart(config, 1);
      toast.success(`${item.name} added to cart!`);
    } catch (error) {
      toast.error("Failed to add to cart");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-[#0A0A0A]">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-[#1a3a1a] to-[#0A0A0A] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm mb-6">
              <Leaf className="w-4 h-4" />
              <span>Eco-Friendly Initiative</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
              Clearance <span className="text-green-400">Enclosures</span>
            </h1>
            
            <p className="text-xl text-[#F4F1DE]/70 max-w-3xl mx-auto mb-8">
              Quality enclosures crafted from overstock and surplus timber. 
              Same premium build, reduced environmental impact, better prices.
            </p>

            {/* Eco Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
              <div className="bg-[#121212]/80 border border-green-500/20 p-6 rounded-lg">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Recycle className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-[#F4F1DE] mb-2">Reduce Waste</h3>
                <p className="text-[#F4F1DE]/60 text-sm">
                  We repurpose surplus timber that would otherwise go unused, minimizing waste in our workshop.
                </p>
              </div>

              <div className="bg-[#121212]/80 border border-green-500/20 p-6 rounded-lg">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TreePine className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-[#F4F1DE] mb-2">Save Trees</h3>
                <p className="text-[#F4F1DE]/60 text-sm">
                  By using overstock materials, we reduce demand for newly harvested timber.
                </p>
              </div>

              <div className="bg-[#121212]/80 border border-green-500/20 p-6 rounded-lg">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Tag className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-[#F4F1DE] mb-2">Great Value</h3>
                <p className="text-[#F4F1DE]/60 text-sm">
                  Save up to 35% on premium enclosures while supporting sustainable practices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clearance Items */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
              Available Now
            </h2>
            <p className="text-[#F4F1DE]/60 mt-1">
              Limited stock - once they're gone, they're gone!
            </p>
          </div>
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Sparkles className="w-4 h-4" />
            <span>{CLEARANCE_ITEMS.reduce((acc, item) => acc + item.stock, 0)} items available</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CLEARANCE_ITEMS.map((item) => (
            <div 
              key={item.id}
              className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden hover:border-green-500/30 transition-colors"
            >
              {/* Color Preview */}
              <div 
                className="h-32 relative"
                style={{ 
                  backgroundColor: WOOD_STAINS_HEX[item.stainKey],
                  backgroundImage: `repeating-linear-gradient(
                    90deg,
                    transparent,
                    transparent 2px,
                    rgba(0,0,0,0.1) 2px,
                    rgba(0,0,0,0.1) 4px
                  )`
                }}
              >
                {/* Discount Badge */}
                <div className="absolute top-3 right-3 bg-green-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                  -{item.discount}%
                </div>
                
                {/* Stock Badge */}
                <div className="absolute bottom-3 left-3 bg-[#0A0A0A]/80 backdrop-blur text-[#F4F1DE]/80 text-xs px-2 py-1 rounded">
                  <Package className="w-3 h-3 inline mr-1" />
                  {item.stock} in stock
                </div>

                {/* Shape indicator */}
                <div className="absolute bottom-3 right-3 bg-[#0A0A0A]/80 backdrop-blur text-[#F4F1DE]/60 text-xs px-2 py-1 rounded capitalize">
                  {item.shape}
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-lg font-bold text-[#F4F1DE] mb-1">{item.name}</h3>
                <p className="text-[#F4F1DE]/60 text-sm mb-3">{item.description}</p>
                
                {/* Dimensions */}
                <div className="flex items-center gap-4 text-xs text-[#F4F1DE]/50 mb-3">
                  <span>{item.dimensions.width}ft × {item.dimensions.height}ft × {item.dimensions.depth}ft</span>
                  <span>•</span>
                  <span>{item.stain}</span>
                </div>

                {/* Timber Source */}
                <div className="bg-green-500/10 border border-green-500/20 rounded p-2 mb-4">
                  <p className="text-green-400 text-xs flex items-start gap-2">
                    <Leaf className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{item.timberSource}</span>
                  </p>
                </div>

                {/* Pricing */}
                <div className="flex items-end justify-between mb-4">
                  <div>
                    <p className="text-[#F4F1DE]/40 text-sm line-through">
                      £{item.originalPrice.toFixed(2)}
                    </p>
                    <p className="text-2xl font-bold text-green-400">
                      £{item.clearancePrice.toFixed(2)}
                    </p>
                  </div>
                  <p className="text-green-400 text-sm font-medium">
                    Save £{(item.originalPrice - item.clearancePrice).toFixed(2)}
                  </p>
                </div>

                {/* Add to Cart */}
                <Button
                  onClick={() => handleAddToCart(item)}
                  disabled={loadingId === item.id || item.stock === 0}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  {loadingId === item.id ? (
                    <span className="spinner" />
                  ) : item.stock === 0 ? (
                    "Sold Out"
                  ) : (
                    <>
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Add to Cart
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-green-500/10 to-green-600/10 border border-green-500/20 rounded-lg p-8 max-w-3xl mx-auto">
            <Recycle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#F4F1DE] mb-2">
              Can't find what you're looking for?
            </h2>
            <p className="text-[#F4F1DE]/60 mb-6">
              Design your own custom enclosure with our configurator. We use sustainably sourced 
              materials and reptile-safe finishes on all our builds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/configurator")}
                className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
              >
                Build Custom Enclosure
              </Button>
              <Button
                onClick={() => navigate("/contact")}
                variant="outline"
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
              >
                Request Eco Options
              </Button>
            </div>
          </div>
        </div>

        {/* Environmental Commitment */}
        <div className="mt-16 border-t border-white/10 pt-12">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-lg font-bold text-[#F4F1DE] mb-4">Our Environmental Commitment</h3>
            <p className="text-[#F4F1DE]/60 text-sm leading-relaxed">
              At ReptiCraft, we're committed to reducing our environmental footprint. Our clearance 
              programme is just one way we minimize waste. We also use water-based, reptile-safe stains, 
              source timber from certified sustainable suppliers, and continuously look for ways to 
              make our manufacturing process more eco-friendly. Every clearance purchase helps us 
              move towards a more sustainable future.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClearancePage;
